import {
  useCreateNotificationMutation,
  useDeleteNotificationMutation,
  useGetNotificationsQuery,
} from "@api/notification";
import Header from "@components/Header";
import { ModalVariant, useModal } from "@components/Modal";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/16/solid";
import { BellIcon } from "@heroicons/react/24/outline";
import { useToastEffect } from "@hooks/hooks";
import {
  CreateNotificationDTO,
  NotificationDTO,
} from "@onetutor/shared/dto/notification.dto";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const NotificationManagementPage = () => {
  const { t } = useTranslation(["notification", "toast", "header", "commons"]);

  const { Modal: DeleteModal, openModal: openDeleteModal } = useModal();

  const [selectedNotification, setSelectedNotification] =
    useState<NotificationDTO>({ id: "", title: "", content: "" });
  const [newNotification, setNewNotification] = useState<CreateNotificationDTO>(
    {
      title: "",
      content: "",
    },
  );

  const { data: notifications } = useGetNotificationsQuery();

  const [
    deleteNotification,
    { isError: deleteIsError, isSuccess: deleteIsSuccess, error: deleteError },
  ] = useDeleteNotificationMutation();

  const [
    createNotification,
    { isError: createIsError, isSuccess: createIsSuccess, error: createError },
  ] = useCreateNotificationMutation();

  useToastEffect({
    isError: deleteIsError,
    isSuccess: deleteIsSuccess,
    error: deleteError,
    successMessage: t("notification.successDelete", { ns: "toast" }),
  });

  useToastEffect({
    isError: createIsError,
    isSuccess: createIsSuccess,
    error: createError,
    successMessage: t("notification.successCreate", { ns: "toast" }),
  });

  const handleDeleteClick = (notification: NotificationDTO) => {
    setSelectedNotification(notification);
    openDeleteModal();
  };

  const handleCreateNotification = () => {
    createNotification(newNotification);
    setNewNotification({
      title: "",
      content: "",
    });
  };

  const notificationTitleInputId = useId();
  const notificationContentTextareaId = useId();

  return (
    <Header
      breadcrumbs={[
        {
          name: t("notifications"),
        },
      ]}
    >
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center">
          <BellIcon className="mr-2 h-8 w-8" />
          <h1 className="font-bold text-2xl">{t("notificationManagement")}</h1>
        </div>
      </div>

      <div className="mb-8">
        <div className="collapse bg-neutral shadow-sm">
          <input type="checkbox" />
          <div className="collapse-title flex items-center font-medium text-xl">
            <PlusCircleIcon className="mr-2 h-6 w-6" />
            {t("createNewNotification")}
          </div>
          <div className="collapse-content">
            <form
              className="form-control w-full max-w-2xl p-2"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateNotification();
              }}
            >
              <div className="mb-4">
                <label htmlFor={notificationTitleInputId}>
                  <span className="font-medium">{t("notificationTitle")}</span>
                </label>
                <input
                  id={notificationTitleInputId}
                  type="text"
                  required
                  className="input input-bordered w-full"
                  value={newNotification.title}
                  onChange={(e) =>
                    setNewNotification({
                      ...newNotification,
                      title: e.target.value,
                    })
                  }
                />
              </div>

              <div className="mb-4">
                <label htmlFor={notificationContentTextareaId}>
                  <span className="font-medium">
                    {t("notificationContent")}
                  </span>
                </label>
                <textarea
                  id={notificationContentTextareaId}
                  required
                  className="textarea textarea-bordered min-h-24 w-full"
                  value={newNotification.content}
                  onChange={(e) =>
                    setNewNotification({
                      ...newNotification,
                      content: e.target.value,
                    })
                  }
                />
              </div>

              <button type="submit" className="btn btn-primary w-full">
                {t("createNotification")}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {notifications?.map((notification) => (
          <div key={notification.id} className="card bg-base-200 shadow-lg">
            <div className="card-body">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="card-title">{notification.title}</h2>
                  <div className="prose-markdown">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {notification.content}
                    </ReactMarkdown>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm btn-circle"
                  onClick={() => handleDeleteClick(notification)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <DeleteModal
        title={t("modalDeleteNotification")}
        confirmVariant={ModalVariant.DESTRUCTIVE}
        onConfirm={() => {
          if (selectedNotification) {
            deleteNotification(selectedNotification.id);
            setSelectedNotification({ id: "", title: "", content: "" });
          }
        }}
        onCancel={() =>
          setSelectedNotification({ id: "", title: "", content: "" })
        }
      >
        {t("modalDeleteNotificationContent", {
          title: selectedNotification?.title,
        })}
      </DeleteModal>
    </Header>
  );
};

export default NotificationManagementPage;
